/**
 * Auth State Machine
 *
 * Single source of truth for authentication state transitions.
 * All routing and UI decisions MUST key off this state machine.
 */

// -----------------------------------------------------------------------------
// AUTH STATES
// -----------------------------------------------------------------------------

/**
 * Explicit auth states - the client MUST be in exactly one of these states
 */
export type AuthState =
  | 'BOOTING'           // Initializing; reading persisted session
  | 'UNAUTHENTICATED'   // No session present
  | 'AUTHENTICATED'     // Session present; user active and can access clinical
  | 'GATED'             // Session present; user blocked from clinical features
  | 'ERROR';            // Unrecoverable; show recovery UI

/**
 * Reasons why a user might be gated (blocked from clinical features)
 */
export type GatingReason =
  | 'PENDING_APPROVAL'      // Waiting for admin approval
  | 'ONBOARDING_REQUIRED'   // Must complete onboarding
  | 'SUSPENDED'             // Account suspended by admin
  | 'REJECTED'              // Application rejected
  | 'EXPIRED'               // Trial/subscription expired
  | 'UNVERIFIED';           // Email not verified

// -----------------------------------------------------------------------------
// AUTH EVENTS (for observability)
// -----------------------------------------------------------------------------

/**
 * Auth events for logging/observability (no PHI)
 */
export type AuthEvent =
  // Boot events
  | 'AUTH_BOOT_START'
  | 'AUTH_BOOT_SESSION_PRESENT'
  | 'AUTH_BOOT_NO_SESSION'
  | 'AUTH_BOOT_TIMEOUT'
  // Callback events
  | 'AUTH_CALLBACK_START'
  | 'AUTH_CALLBACK_EXCHANGED'
  | 'AUTH_CALLBACK_FAILED'
  | 'AUTH_CALLBACK_ALREADY_AUTHENTICATED'
  // State change events
  | 'AUTH_STATE_CHANGE'
  | 'AUTH_LOGIN'
  | 'AUTH_LOGOUT'
  // Gate events
  | 'GATE_CHECK_START'
  | 'GATE_RESULT'
  // Error events
  | 'AUTH_LOOP_DETECTED'
  | 'AUTH_STUCK_TIMEOUT'
  | 'AUTH_REFRESH_FAILED'
  | 'AUTH_ERROR';

// -----------------------------------------------------------------------------
// BOOTSTRAP RESPONSE (from /me endpoint)
// -----------------------------------------------------------------------------

/**
 * Account status from the server
 */
export type AccountStatus =
  | 'unverified'
  | 'onboarding_required'
  | 'pending_approval'
  | 'pending'
  | 'trial'
  | 'active'
  | 'expired'
  | 'suspended'
  | 'rejected'
  | 'free'
  | 'past_due';

/**
 * Onboarding status from the server
 */
export type OnboardingStatus = 'not_started' | 'in_progress' | 'complete';

/**
 * Response from the /me (session-bootstrap) endpoint
 * This is the server-truth for post-login gating
 */
export interface SubscriptionInfo {
  plan_id: string | null;
  plan_name: string | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface Entitlements {
  reports_limit: number | null;
  can_use_all_templates: boolean;
  can_export_clean: boolean;
  custom_macros_limit: number | null;
  daily_reports_used: number;
  daily_reports_remaining: number | null;
  grace_period_active: boolean;
  grace_period_ends_at: string | null;
}

export interface BootstrapResponse {
  user_id: string;
  email: string;
  account_status: AccountStatus;
  onboarding_status: OnboardingStatus;
  role: 'radiologist' | 'admin' | null;
  is_admin: boolean;
  can_access_clinical: boolean;
  gating_reason: GatingReason | null;
  capabilities: string[];
  /** True if user should see onboarding reminder banner (active but incomplete onboarding) */
  needs_onboarding_reminder: boolean;
  /** Subscription details (null if no active subscription) */
  subscription: SubscriptionInfo | null;
  /** Feature entitlements based on account status */
  entitlements: Entitlements;
}

// -----------------------------------------------------------------------------
// AUTH CONTEXT STATE
// -----------------------------------------------------------------------------

/**
 * Complete auth context state - includes both Supabase session and server-truth
 */
export interface AuthContextState {
  // State machine
  authState: AuthState;
  gatingReason: GatingReason | null;

  // Supabase session
  session: import('@supabase/supabase-js').Session | null;
  user: import('@supabase/supabase-js').User | null;

  // Server-truth (from /me endpoint)
  bootstrap: BootstrapResponse | null;

  // Derived properties (for convenience)
  role: 'radiologist' | 'admin' | null;
  isAdmin: boolean;
  canAccessClinical: boolean;

  // Error state
  error: string | null;
}

// -----------------------------------------------------------------------------
// STATE TRANSITIONS
// -----------------------------------------------------------------------------

/**
 * Determine the auth state based on session and bootstrap data
 */
export function deriveAuthState(
  session: import('@supabase/supabase-js').Session | null,
  bootstrap: BootstrapResponse | null
): { authState: AuthState; gatingReason: GatingReason | null } {
  // No session = unauthenticated
  if (!session) {
    return { authState: 'UNAUTHENTICATED', gatingReason: null };
  }

  // Session exists but no bootstrap = still booting
  if (!bootstrap) {
    return { authState: 'BOOTING', gatingReason: null };
  }

  // Check gating conditions
  if (!bootstrap.can_access_clinical) {
    return {
      authState: 'GATED',
      gatingReason: bootstrap.gating_reason || 'PENDING_APPROVAL'
    };
  }

  // All clear
  return { authState: 'AUTHENTICATED', gatingReason: null };
}

/**
 * Determine gating reason from account/onboarding status
 */
export function deriveGatingReason(
  accountStatus: AccountStatus | null,
  onboardingStatus: OnboardingStatus | null
): GatingReason | null {
  // Check onboarding first
  if (onboardingStatus && onboardingStatus !== 'complete') {
    return 'ONBOARDING_REQUIRED';
  }

  // Check account status
  if (!accountStatus) {
    return 'ONBOARDING_REQUIRED';
  }

  switch (accountStatus) {
    case 'unverified':
      return 'UNVERIFIED';
    case 'onboarding_required':
      return 'ONBOARDING_REQUIRED';
    case 'pending_approval':
    case 'pending':
      return 'PENDING_APPROVAL';
    case 'suspended':
      return 'SUSPENDED';
    case 'rejected':
      return 'REJECTED';
    case 'expired':
      return 'EXPIRED';
    case 'trial':
    case 'active':
    case 'free':
    case 'past_due':
      return null; // Not gated
    default:
      return 'PENDING_APPROVAL'; // Safe default
  }
}

// -----------------------------------------------------------------------------
// CONSTANTS
// -----------------------------------------------------------------------------

/**
 * Timeout for boot phase (reading persisted session)
 * If exceeded, show recovery UI
 */
export const AUTH_BOOT_TIMEOUT_MS = 3000;

/**
 * Timeout for callback exchange
 */
export const AUTH_CALLBACK_TIMEOUT_MS = 5000;

/**
 * Maximum auth state changes within a window to detect loops
 */
export const AUTH_LOOP_THRESHOLD = 10;
export const AUTH_LOOP_WINDOW_MS = 30000;

/**
 * Session storage key for detecting auth callback
 */
export const AUTH_CALLBACK_PENDING_KEY = 'auth_callback_pending';
